import { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Menu as MenuIcon,
  ChevronRight,
  ChevronDown,
  Trash2,
  RefreshCw,
  Loader2,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import Swal from "sweetalert2";

const RolesPermisos = () => {
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    loadUserInfo();
    loadInitialData();
  }, []);

  const getApiHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const getBackendUrl = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace("/api", "");
    }
    const isDevelopment = process.env.NODE_ENV === "development";
    return isDevelopment ? "http://localhost:8000" : window.location.origin;
  };

  const loadUserInfo = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/user`, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const user = await response.json();
        setUserInfo(user);
        console.log("👤 Usuario logueado:", user);
      }
    } catch (error) {
      console.error("❌ Error cargando info de usuario:", error);
    }
  };

  const loadInitialData = async () => {
    setLoading(true);
    console.log("🔄 Iniciando carga de datos filtrados...");

    try {
      const backendUrl = getBackendUrl();
      const apiUrl = `${backendUrl}/api`;
      const headers = getApiHeaders();

      const [rolesRes, menusRes] = await Promise.all([
        fetch(`${apiUrl}/roles-permisos/roles`, { headers }),
        fetch(`${apiUrl}/roles-permisos/menus`, { headers }),
      ]);

      console.log("📊 Roles response:", rolesRes.status);
      console.log("📊 Menus response:", menusRes.status);

      if (rolesRes.ok && menusRes.ok) {
        const rolesData = await rolesRes.json();
        const menusData = await menusRes.json();

        console.log("✅ Roles filtrados:", rolesData);
        console.log("✅ Menús filtrados:", menusData);

        setRoles(rolesData);
        setMenus(menusData);

        // ✅ CAMBIO: Expandir TODOS los menús recursivamente
        const expandAllMenus = (menus) => {
          const ids = new Set();
          const traverse = (menu) => {
            ids.add(menu.id);
            if (menu.children && menu.children.length > 0) {
              menu.children.forEach(traverse);
            }
          };
          menus.forEach(traverse);
          return ids;
        };

        setExpandedMenus(expandAllMenus(menusData));

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Datos cargados correctamente",
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        throw new Error("Error cargando datos del servidor");
      }
    } catch (error) {
      console.error("❌ Error completo:", error);
      Swal.fire({
        title: "Error de conexión",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      const backendUrl = getBackendUrl();
      const apiUrl = `${backendUrl}/api`;
      const url = `${apiUrl}/roles-permisos/permisos/${roleId}`;

      console.log("📋 Cargando permisos para rol:", roleId);

      const response = await fetch(url, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Permisos cargados:", data);

        const permissionsMap = {};
        data.forEach((perm) => {
          permissionsMap[perm.menu_id] = {
            can_view: perm.can_view,
            can_create: perm.can_create,
            can_edit: perm.can_edit,
            can_delete: perm.can_delete,
            // ✅ AGREGAR: Permisos de evidencias
            can_view_evidencias: perm.can_view_evidencias || false,
            can_create_evidencias: perm.can_create_evidencias || false,
            can_edit_evidencias: perm.can_edit_evidencias || false,
            can_delete_evidencias: perm.can_delete_evidencias || false,
          };
        });
        setPermissions(permissionsMap);
      } else if (response.status === 403) {
        Swal.fire({
          title: "Acceso Denegado",
          text: "No tienes permisos para ver este rol",
          icon: "error",
        });
      } else {
        throw new Error("Error cargando permisos");
      }
    } catch (error) {
      console.error("❌ Error cargando permisos:", error);
      Swal.fire("Error", "No se pudieron cargar los permisos", "error");
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    loadRolePermissions(role.id);
  };

  const toggleMenuExpansion = (menuId) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  const updatePermission = async (menuId, permissionType, value) => {
    if (!selectedRole) return;

    setSaving(true);
    const currentPerms = permissions[menuId] || {};
    const newPerms = { ...currentPerms, [permissionType]: value };

    try {
      const backendUrl = getBackendUrl();
      const apiUrl = `${backendUrl}/api`;
      const url = `${apiUrl}/roles-permisos/asignar`;

      const body = {
        role_id: selectedRole.id,
        menu_id: menuId,
        ...newPerms,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setPermissions((prev) => ({
          ...prev,
          [menuId]: newPerms,
        }));

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Permiso actualizado",
          showConfirmButton: false,
          timer: 1500,
        });
      } else if (response.status === 403) {
        Swal.fire({
          title: "Acceso Denegado",
          text: "No tienes permisos para modificar este rol",
          icon: "error",
        });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error:", error);
      Swal.fire("Error", `No se pudo actualizar el permiso`, "error");
    } finally {
      setSaving(false);
    }
  };

  const revokeAllPermissions = async (menuId) => {
    if (!selectedRole) return;

    const result = await Swal.fire({
      title: "¿Revocar todos los permisos?",
      text: "Se eliminarán todos los permisos para este menú",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, revocar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const backendUrl = getBackendUrl();
        const apiUrl = `${backendUrl}/api`;
        const url = `${apiUrl}/roles-permisos/revocar`;

        const body = {
          role_id: selectedRole.id,
          menu_id: menuId,
        };

        const response = await fetch(url, {
          method: "DELETE",
          headers: getApiHeaders(),
          body: JSON.stringify(body),
        });

        if (response.ok) {
          setPermissions((prev) => {
            const newPerms = { ...prev };
            delete newPerms[menuId];
            return newPerms;
          });

          Swal.fire("Revocado", "Permisos eliminados exitosamente", "success");
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("❌ Error:", error);
        Swal.fire("Error", "No se pudieron revocar los permisos", "error");
      }
    }
  };

  const getRoleVariant = (role) => {
    // Si es rol de sistema (superadmin)
    if (role.is_system_role) {
      return "destructive";
    }

    // Para roles de empresa, verificar si es admin por nombre
    const adminNames = ["admin_empresa", "admin", "administrador"];
    const isAdmin = adminNames.some((name) =>
      role.name.toLowerCase().includes(name),
    );

    return isAdmin ? "destructive" : "default";
  };

  const PermissionCheckbox = ({ menuId, permissionType, label }) => {
    const currentPerms = permissions[menuId] || {};
    const isChecked = currentPerms[permissionType] || false;

    return (
      <div className="flex items-center space-x-1">
        <Checkbox
          id={`${menuId}-${permissionType}`}
          checked={isChecked}
          onCheckedChange={(checked) =>
            updatePermission(menuId, permissionType, checked)
          }
          disabled={saving}
        />
        <label
          htmlFor={`${menuId}-${permissionType}`}
          className="text-xs font-medium cursor-pointer"
        >
          {label}
        </label>
      </div>
    );
  };

  const MenuPermissionRow = ({ menu, level = 0 }) => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus.has(menu.id);
    const currentPerms = permissions[menu.id] || {};
    const hasAnyPermission = Object.values(currentPerms).some((p) => p);

    return (
      <div className="space-y-1">
        <div
          className={`
          flex items-center justify-between p-2 rounded-md
          ${
            level === 0
              ? "bg-gray-50 border"
              : level === 1
                ? "bg-gray-25 ml-4"
                : "ml-8"
          }
          ${hasAnyPermission ? "ring-1 ring-blue-200 bg-blue-50/30" : ""}
        `}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {hasChildren && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => toggleMenuExpansion(menu.id)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
            )}
            {!hasChildren && <div className="w-6" />}

            <div className="flex-1 min-w-0">
              <span
                className={`font-medium text-sm truncate block ${
                  level === 0 ? "text-gray-900" : "text-gray-700"
                }`}
              >
                {menu.name}
              </span>
              {menu.route && (
                <span className="text-xs text-gray-500 truncate block">
                  {menu.route}
                </span>
              )}
            </div>

            {level === 0 && (
              <Badge
                variant={hasAnyPermission ? "default" : "outline"}
                className="text-xs"
              >
                Nivel {level + 1}
              </Badge>
            )}
          </div>

          {/* Checkboxes de permisos */}
          <div className="flex items-center space-x-3">
            <PermissionCheckbox
              menuId={menu.id}
              permissionType="can_view"
              label="Ver"
            />
            <PermissionCheckbox
              menuId={menu.id}
              permissionType="can_create"
              label="Crear"
            />
            <PermissionCheckbox
              menuId={menu.id}
              permissionType="can_edit"
              label="Editar"
            />
            <PermissionCheckbox
              menuId={menu.id}
              permissionType="can_delete"
              label="Eliminar"
            />

            {hasAnyPermission && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                onClick={() => revokeAllPermissions(menu.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Permisos especiales de evidencias para "viajes" */}
        {menu.slug === "viajes" && (
          <div
            className={`mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200 ${
              level === 1 ? "ml-4" : level === 2 ? "ml-8" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-semibold text-purple-900">
                  📦 Permisos de Evidencias
                </span>
                <Badge variant="outline" className="text-xs">
                  Funcionalidad adicional
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                <PermissionCheckbox
                  menuId={menu.id}
                  permissionType="can_view_evidencias"
                  label="Ver Evid."
                />
                <PermissionCheckbox
                  menuId={menu.id}
                  permissionType="can_create_evidencias"
                  label="Subir Evid."
                />
                <PermissionCheckbox
                  menuId={menu.id}
                  permissionType="can_edit_evidencias"
                  label="Editar Evid."
                />
                <PermissionCheckbox
                  menuId={menu.id}
                  permissionType="can_delete_evidencias"
                  label="Eliminar Evid."
                />
              </div>
            </div>
          </div>
        )}

        {/* Renderizado recursivo de hijos */}
        {hasChildren && isExpanded && (
          <div className="space-y-1">
            {menu.children.map((child) => (
              <MenuPermissionRow
                key={child.id}
                menu={child}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando datos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header con info del usuario */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">
                Gestión de Roles y Permisos
              </CardTitle>
              {userInfo && userInfo.empresa && (
                <Badge variant="outline" className="ml-2">
                  {userInfo.empresa.nombre}
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadInitialData}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Actualizar
            </Button>

            {/* ✅ NUEVO: Botón expandir/colapsar todos */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (expandedMenus.size > 0) {
                  // Colapsar todos
                  setExpandedMenus(new Set());
                } else {
                  // Expandir todos
                  const expandAllMenus = (menus) => {
                    const ids = new Set();
                    const traverse = (menu) => {
                      ids.add(menu.id);
                      if (menu.children && menu.children.length > 0) {
                        menu.children.forEach(traverse);
                      }
                    };
                    menus.forEach(traverse);
                    return ids;
                  };
                  setExpandedMenus(expandAllMenus(menus));
                }
              }}
            >
              {expandedMenus.size > 0 ? (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Colapsar Todos
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Expandir Todos
                </>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Alerta informativa sin componente Alert */}
      {userInfo && userInfo.empresa_id && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-900">
                Estás gestionando los roles de tu empresa. Solo puedes ver y
                modificar los roles y menús correspondientes a{" "}
                <strong>
                  {userInfo.empresa?.tipo || "tu tipo de empresa"}
                </strong>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Panel de Roles */}
        <Card className="lg:col-span-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Roles ({roles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {roles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No hay roles disponibles</p>
              </div>
            ) : (
              roles.map((role) => (
                <div
                  key={role.id}
                  className={`
      p-3 rounded-lg border cursor-pointer transition-all
      ${
        selectedRole?.id === role.id
          ? "ring-2 ring-blue-500 bg-blue-50 border-blue-200"
          : "hover:bg-gray-50 border-gray-200"
      }
    `}
                  onClick={() => handleRoleSelect(role)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm capitalize truncate">
                          {role.name}
                        </span>
                        <Badge
                          variant={getRoleVariant(role)}
                          className="text-xs"
                        >
                          {role.is_system_role ? "Sistema" : "Empresa"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 truncate">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Panel de Permisos */}
        <Card className="lg:col-span-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <div className="flex items-center">
                <MenuIcon className="h-4 w-4 mr-2" />
                Permisos por Menú
                {selectedRole && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {selectedRole.name}
                  </Badge>
                )}
              </div>
              {saving && (
                <div className="flex items-center text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  <span className="text-xs">Guardando...</span>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedRole ? (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Selecciona un rol para gestionar sus permisos</p>
              </div>
            ) : menus.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MenuIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No hay menús disponibles para tu tipo de empresa</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Header de permisos */}
                <div className="flex items-center justify-end space-x-3 text-xs font-medium text-gray-500 pr-2 mb-2">
                  <span>Ver</span>
                  <span>Crear</span>
                  <span>Editar</span>
                  <span>Eliminar</span>
                  <span className="w-6"></span>
                </div>

                {menus.map((menu) => (
                  <MenuPermissionRow key={menu.id} menu={menu} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RolesPermisos;
